import { DatePicker } from '@mui/lab';
import { Grid, useEventCallback } from '@mui/material';
import { DialogContentText, useTheme, useMediaQuery } from '@mui/material';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { memo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialog } from 'shell/components/shared/dialog';
import { PmiDialogContent } from 'shell/components/shared/dialog/content';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import PmiTextField from 'shell/components/shared/text-field';
import * as yup from 'yup';

export interface ModalFormVM {
  dateRegistration?: DateTime | null;
  rejectReason?: string;
}

interface ArchiveAnnotationsModalComponentProps {
  open: boolean;
  onClose: (open?: boolean) => void;
  onConfirm: (values: ModalFormVM) => void;
  message: string;
  isRegistration?: boolean;
  isReject?: boolean;
}

const ArchiveAnnotationsModalComponent = ({
  open,
  onClose,
  onConfirm,
  message,
  isRegistration = false,
  isReject = false,
}: ArchiveAnnotationsModalComponentProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const onSubmit = useEventCallback((values: ModalFormVM) => {
    onConfirm(values);
    formContext.resetForm();
  });

  const validationSchema = isRegistration
    ? yup.object({
        dateRegistration: yup
          .date()
          .typeError('Data non valida')
          .nullable()
          .required('Campo obbligatorio'),
      })
    : isReject
    ? yup.object({
        rejectReason: yup.string().required('Campo obbligatorio'),
      })
    : yup.object({});

  const formContext = useFormik<ModalFormVM>({
    initialValues: { dateRegistration: null, rejectReason: '' },
    validationSchema,
    validateOnMount: true,
    onSubmit,
  });

  const handleClose = useEventCallback(() => {
    formContext.resetForm();
    onClose(false);
  });

  return open ? (
    <PmiDialog
      open={open}
      fullScreen={fullScreen}
      onClose={handleClose}
      aria-labelledby="domain-value-form-modal"
      aria-describedby="domain-value-form-modal"
    >
      <PmiDialogTitle id="alert-dialog-title" color="white" backgroundColor="cdmBrand">
        Attenzione!
      </PmiDialogTitle>
      <PmiDialogContent>
        <DialogContentText id="alert-dialog-description" color="grey">
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="center"
            alignItems="center"
            paddingBottom="16px"
          >
            {message}
          </Grid>
          {isRegistration ? (
            <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Data registrazione *"
                  aria-label="Formazione da"
                  value={formContext.values.dateRegistration || null}
                  onChange={(date) => formContext.setFieldValue('dateRegistration', date)}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="dateRegistration"
                      error={
                        formContext.touched.dateRegistration &&
                        Boolean(formContext.errors.dateRegistration)
                      }
                      helperText={formContext.errors.dateRegistration}
                      fullWidth
                    />
                  )}
                />
              </Grid>{' '}
            </Grid>
          ) : undefined}
          {isReject ? (
            <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
              <Grid item xs={12}>
                <PmiTextField
                  id="rejectReason"
                  label="Motivo rifiuto *"
                  aria-label="Motivo rifiuto"
                  multiline
                  rows={3}
                  value={formContext.values.rejectReason || ''}
                  onChange={(e) => formContext.setFieldValue('rejectReason', e.target.value)}
                  error={
                    formContext.touched.rejectReason && Boolean(formContext.errors.rejectReason)
                  }
                  helperText={formContext.errors.rejectReason}
                  fullWidth
                />
              </Grid>{' '}
            </Grid>
          ) : undefined}
        </DialogContentText>
      </PmiDialogContent>
      <div className="pmi-d-flex pmi-justify-content-end pmi-pb-3 pmi-px-3">
        <div className="pmi-mr-3 pmi-pr-1" style={{ width: 'auto' }}>
          <PmiButton
            fullWidth
            onClick={() => formContext.handleSubmit()}
            variant="outlined"
            color="primary"
            disabled={isReject && !formContext.isValid}
          >
            Conferma
          </PmiButton>
        </div>
        <div style={{ width: 'auto' }}>
          <PmiButton fullWidth onClick={handleClose} variant="outlined" color="cdmBrand">
            Annulla
          </PmiButton>
        </div>
      </div>
    </PmiDialog>
  ) : (
    <></>
  );
};

ArchiveAnnotationsModalComponent.displayName = 'Archive Annotations Modal component';

const ArchiveAnnotationsModal = memo(ArchiveAnnotationsModalComponent);

export default ArchiveAnnotationsModal as typeof ArchiveAnnotationsModalComponent;
